// AdminAlerts — Block 7 lite (P1). Lists system alerts and lets admins mark
// them as seen (any role) or resolved with a note (super_admin only).
import React, { useEffect, useMemo, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import { Loader2, Bell, CheckCircle2, Eye, AlertOctagon } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const SeverityBadge = ({ severity }) => {
  const map = {
    critical: ['bg-[#FEE2E2] text-[#7F1D1D]', 'Crítico'],
    warning: ['bg-[#FEF3C7] text-[#92400E]', 'Atenção'],
    info: ['bg-[#DBEAFE] text-[#1E3A5F]', 'Info'],
  };
  const [cls, label] = map[severity] || map.info;
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>;
};

const StatusBadge = ({ status }) => {
  const map = {
    novo: ['bg-[#FEE2E2] text-[#7F1D1D]', 'Novo'],
    visto: ['bg-[#FEF3C7] text-[#92400E]', 'Visto'],
    resolvido: ['bg-[#DCFCE7] text-[#006030]', 'Resolvido'],
  };
  const [cls, label] = map[status] || ['bg-[#F4F6F8] text-[#475569]', status || 'n/d'];
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>;
};

const AdminAlerts = () => {
  const { token, isSuper } = useAdminAuth();
  const ax = useMemo(() => adminAxios(token), [token]);

  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ novo: 0, visto: 0, resolvido: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await ax.get(`/api/admin/alerts?status_filter=${statusFilter}`);
      setAlerts(r.data?.alerts || []);
      setCounts(r.data?.counts || { novo: 0, visto: 0, resolvido: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Falha ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const markSeen = async (a) => {
    setActing(a.alert_id);
    try {
      await ax.post(`/api/admin/alerts/${a.alert_id}/seen`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Falha ao marcar como visto');
    } finally {
      setActing(null);
    }
  };

  const resolve = async (a) => {
    if (!isSuper) return;
    const nota = window.prompt('Nota de resolução (mínimo 2 caracteres):');
    if (!nota || nota.trim().length < 2) return;
    setActing(a.alert_id);
    try {
      await ax.post(`/api/admin/alerts/${a.alert_id}/resolve`, { nota: nota.trim() });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Falha ao resolver alerta');
    } finally {
      setActing(null);
    }
  };

  const Counter = ({ label, value, color }) => (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: color + '15', color }}>
        <Bell className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#475569]">{label}</p>
        <p className="text-xl font-black text-[#0F1C2E]">{value}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-alerts-page">
      <div>
        <h1 className="text-2xl font-black text-[#0F1C2E]">Alertas do sistema</h1>
        <p className="text-xs text-[#475569]">Eventos que precisam da atenção da operação</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Counter label="Novos" value={counts.novo} color="#DC2626" />
        <Counter label="Em análise" value={counts.visto} color="#F59E0B" />
        <Counter label="Resolvidos" value={counts.resolvido} color="#006030" />
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'Todos' },
          { id: 'novo', label: 'Novos' },
          { id: 'visto', label: 'Em análise' },
          { id: 'resolvido', label: 'Resolvidos' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded text-xs font-bold ${
              statusFilter === f.id
                ? 'bg-[#1E3A5F] text-white'
                : 'bg-[#F4F6F8] text-[#475569] hover:bg-[#E2E8F0]'
            }`}
            data-testid={`alerts-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>
      ) : err ? (
        <p className="text-sm text-[#DC2626]">{err}</p>
      ) : alerts.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="alerts-empty">
          <CheckCircle2 className="w-10 h-10 text-[#006030] mx-auto mb-2" />
          <p className="text-sm font-bold text-[#0F1C2E]">Nada fora do padrão</p>
          <p className="text-xs text-[#94A3B8] mt-1">
            O sistema avisará por aqui quando algo importante acontecer (pagamentos travados, picos de erro, etc.).
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="alerts-list">
          {alerts.map((a) => (
            <li key={a.alert_id}
                className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3"
                data-testid={`alert-${a.alert_id}`}>
              <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                   style={{
                     background: (a.severity === 'critical' ? '#DC2626' : a.severity === 'warning' ? '#F59E0B' : '#1E3A5F') + '15',
                     color: a.severity === 'critical' ? '#DC2626' : a.severity === 'warning' ? '#F59E0B' : '#1E3A5F',
                   }}>
                <AlertOctagon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-bold text-[#0F1C2E]">{a.titulo || 'Alerta'}</h4>
                  <SeverityBadge severity={a.severity} />
                  <StatusBadge status={a.status} />
                </div>
                {a.mensagem && <p className="text-xs text-[#475569] mt-1">{a.mensagem}</p>}
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  {fmtDate(a.ts)}
                  {a.visto_em && ` · visto por ${a.visto_por} em ${fmtDate(a.visto_em)}`}
                  {a.resolvido_em && ` · resolvido por ${a.resolvido_por} em ${fmtDate(a.resolvido_em)}`}
                </p>
                {a.nota_resolucao && (
                  <p className="text-[11px] text-[#0F1C2E] mt-1 italic">"{a.nota_resolucao}"</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {a.status === 'novo' && (
                  <button
                    onClick={() => markSeen(a)}
                    disabled={acting === a.alert_id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] disabled:opacity-50"
                    data-testid={`alert-seen-${a.alert_id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> Marcar como visto
                  </button>
                )}
                {isSuper && a.status !== 'resolvido' && (
                  <button
                    onClick={() => resolve(a)}
                    disabled={acting === a.alert_id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold bg-[#006030] hover:bg-[#004020] text-white disabled:opacity-50"
                    data-testid={`alert-resolve-${a.alert_id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Resolver
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminAlerts;
